import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Quill from "quill";
import 'quill/dist/quill.snow.css';
import "./ContractEdit.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { ReactSVG } from "react-svg";
import downloadIcon from '../../Assets/icons/download.svg';
import { formatDistanceToNow } from 'date-fns';
import { nl, fr } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import ApiContext from "../../Api/ApiContext";

interface ContractEditProps {
    documentId: string;
    documentName: string;
    textToCopy: string | null;
    setTextToCopy: (text: string | null) => void;
}

const assignIdToNodes = (nodes: NodeListOf<ChildNode>) => {
    for(let i = 0; i < nodes.length; i++) {
        let node = nodes[i];
        if (node.nodeType === Node.ELEMENT_NODE && !(node as Element).hasAttribute('id')) {
            (node as Element).id = uuidv4();
        }

        if(node.hasChildNodes()) {
            assignIdToNodes(node.childNodes);
        }
    }
}

const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
        assignIdToNodes(mutation.addedNodes as NodeListOf<ChildNode>);
    });
});

let Block = Quill.import('blots/block');

class CustomBlock extends Block {
  split(deep: any, fill: any) {
    let block = super.split(deep, fill);
    if (block.domNode.getAttribute('id')) {
      block.domNode.removeAttribute('id');
      block.domNode.setAttribute('id', uuidv4());
    }
    return block;
  }
}

CustomBlock.blotName = 'block';
CustomBlock.tagName = 'P';

Quill.register(CustomBlock);

const ContractEdit: React.FC<ContractEditProps> = (props) => {

    const { documentId, documentName, textToCopy, setTextToCopy } = props;

    const { getAccessTokenSilently } = useAuth0();
    const quillRef = useRef<HTMLDivElement>(null);

    const [quill, setQuill] = useState<Quill | null>(null);

    const [ lastSavedValue, setLastSavedValue ] = useState<number | null>(null);
    const [ lastSaved, setLastSaved ] = useState<string | null>(null);
    const [ saving, setSaving ] = useState(false);

    const [ originalContent, setOriginalContent ] = useState<string | null>(null);

    const { t, i18n } = useTranslation();

    const api = useContext(ApiContext);

    const formatTimestamp = useCallback((timestamp) => {

        const options = { includeSeconds: true } as any;

        if (i18n.language === 'nl') {
            options.locale = nl;
        }

        if (i18n.language === 'fr') {
            options.locale = fr;
        }

        const dateInAmsterdam = toZonedTime(timestamp, 'Europe/Amsterdam');
    
        const formattedDate = formatDistanceToNow(dateInAmsterdam, options);
    
        return t("saved", {formattedDate});
    }, [i18n.language, t]);

    useEffect(() => {
    
        if (quill && textToCopy) {
            let delta = quill.clipboard.convert(textToCopy);
    
            if (!delta || !delta.ops) {
                console.log('delta or delta.ops is undefined');
                return;
            }
    
            if (!delta.ops[delta.ops.length - 1].insert) {
                delta.ops.push({ insert: "\n" });
            }
    
            const range = quill.getSelection(true);
            let index = range ? range.index : 0;
    
            let editorContentsLength = quill.getLength();
            if (index >= editorContentsLength) {
                console.log(`Index ${index} exceeds editor contents length ${editorContentsLength}`);
                return;
            }
    
            let changeDelta;
        
            if (index > 0 && quill.getLength() > 0) {
                changeDelta = {
                    ops: [
                        { retain: index },
                        ...delta.ops.map(op => {
                            if(op.insert) {
                                return { 
                                    insert: op.insert, 
                                    attributes: { ...op.attributes, background: '#39CB89' } 
                                };
                            } 
                            return op;
                        }),
                    ],
                };
            } else {
                changeDelta = {
                    ops: delta.ops.map(op => {
                        if(op.insert) {
                            return { 
                                insert: op.insert, 
                                attributes: { ...op.attributes, background: '#39CB89' } 
                            };
                        } 
                        return op;
                    }),
                };
            }
            
            quill.updateContents(changeDelta);
    
            api.post('/add-document-change', 
                { documentId, change: changeDelta },
            );

            const now = Date.now();
            setLastSavedValue(now);
            setLastSaved(formatTimestamp(now));
            
            setTextToCopy(null);
        }
    }, [textToCopy, quill, getAccessTokenSilently, documentId, setTextToCopy, formatTimestamp, api]);    
    
    useEffect(() => {
        if (quillRef.current) {
            const quillInstance = new Quill(quillRef.current, {
                theme: 'snow'
            });

            if ('bindings' in quillInstance.keyboard) {
                delete quillInstance.keyboard.bindings[8];
            }

            quillInstance.keyboard.addBinding({ key: 'Backspace' }, function(range, context) {
                let [line] = this.quill.getLine(range.index);
            
                const sendDeltaChange = (changeDelta) => {
                    api.post('/add-document-change', 
                        { documentId, change: changeDelta }
                    );
                    const now = Date.now();
                    setLastSavedValue(now);
                    setLastSaved(formatTimestamp(now));
                }
            
                const buildAndSendChange = (index, length) => {
                    let changeDelta;
                    if (index > 0 && quillInstance.getLength() > 0) {
                        changeDelta = {
                            ops: [
                                { retain: index },
                                { delete: length }
                            ],
                        };
                    } else {
                        changeDelta = { ops: [{ delete: length }] };
                    }
                    sendDeltaChange(changeDelta);
                };
            
                if (range.length > 0) {
                    quillInstance.deleteText(range.index, range.length);
                    buildAndSendChange(range.index, range.length);
                    return;
                }
              
                if (range.index > 0 && range.length === 0) {
                    let [prevLine] = this.quill.getLine(range.index - 1);
                      
                    if (prevLine && line) {
                        let prevId = prevLine.domNode.id;
                  
                        quillInstance.deleteText(range.index - 1, 1);
                        buildAndSendChange(range.index - 1, 1);
                  
                        line.domNode.id = prevId;
                    }
                } else {
                    quillInstance.deleteText(range.index - 1, 1);
                    buildAndSendChange(range.index - 1, 1);
                }
            });                   

            setQuill(quillInstance);

            observer.observe(quillInstance.root, { childList: true, subtree: true });

            let pendingDeltas = null;
            let debounceTimeout = null;
            const debounceMillis = 1000;
            
            quillInstance.on('text-change', function(delta, oldDelta, source) {
                if (source === 'user') {
                    if (!pendingDeltas) {
                        pendingDeltas = [];
                    }
            
                    pendingDeltas.push(delta);
            
                    clearTimeout(debounceTimeout);
                    debounceTimeout = setTimeout(() => {
                        setSaving(true);

                        api.post('/add-document-change', 
                            { documentId, change: {deltas: pendingDeltas} }
                        );

                        const now = Date.now();
                        setLastSavedValue(now);
                        setLastSaved(formatTimestamp(now));
        
                        pendingDeltas = [];
                        setSaving(false);
                    }, debounceMillis);

                    assignIdToNodes(quillInstance.root.childNodes);
                }
            });                  

            if (documentId) {
                api.post('/get-document-changes', 
                    { documentId}
                ).then(res => {
                    quillInstance.clipboard.dangerouslyPasteHTML(res.data.initial);
                    assignIdToNodes(quillInstance.root.childNodes);
                    setTimeout(() => {
                        setOriginalContent(quillInstance.root.innerHTML);
                        res.data.changes.reverse().forEach(obj => {
                            obj.change_delta.deltas?.forEach(delta => {
                                quillInstance.updateContents(delta);
                            });
                            if(obj.change_delta.ops) {
                                quillInstance.updateContents(obj.change_delta);
                            }
                        });

                        const lastChange = res.data.changes[res.data.changes.length - 1];
                        if (lastChange) {
                            const timestampInMillis = lastChange.created * 1000;
                            const dateObject = new Date(timestampInMillis);
                            setLastSavedValue(dateObject.getTime());
                            const formattedTimestamp = formatTimestamp(dateObject);
                            setLastSaved(formattedTimestamp);
                        }
                    }, 1000);
                });
            }
        }

        return () => {
            quillRef.current = null;
        };
    }, [documentId, getAccessTokenSilently, formatTimestamp, api, quillRef.current]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (lastSavedValue) {
                const formattedTimestamp = formatTimestamp(lastSavedValue);
                setLastSaved(formattedTimestamp);
            }
        }, 10000);
        return () => clearInterval(interval);
    }, [lastSavedValue, formatTimestamp]);

    async function download(trackChanges = false) {
        
        const response = await api.post('/download-document', 
                {
                    documentId, 
                    originalContent: originalContent,
                    newContent: quill.root.innerHTML, 
                    trackChanges
                },
                {
                    responseType: 'arraybuffer'  // instructs Axios to expect a stream in the response
                },
            );
        
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName.split('.')[0] + '_LegalFly.docx');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
    }

    return (
        <div className="contractEdit">
            <div className="download"><ReactSVG src={downloadIcon} />
                .docx
                <div className="download-dropdown">
                    <div className="download-dropdown-item" onClick={() => download()} data-click-target="download-doxc" data-click-source="review">Standard .docx</div>
                    <div className="download-dropdown-item" onClick={() => download(true)} data-click-target="download-track-changes-docx" data-click-source="review">Track Changes .docx</div>
                    <div className="download-dropdown-item soon">Review Comments</div>
                </div>
            </div>
            <div ref={quillRef} />
            { lastSavedValue && <div className="last-saved">{ saving ? "Saving..." : `${lastSaved}` }</div> }
        </div>
    );
};

export default ContractEdit;
